import { CarvanaEmblem, CarvanaHorizontal } from '@carvana/ds-icons';
import { Menu } from 'lucide-react';
import React, { useContext, useState } from 'react';
import { Summary, Dashboard } from '|/app/services';
import { useBrand } from '|/brand';
import { SupportContact, SupportContactLink, SupportMfeModal } from '|/components/support-contact';
import { CheckItOutContext } from '|/context';
import MobileNavigationRailContext from '|/context/mobile-navigation-rail-context';
import useAnalyticsContext from '|/hooks/use-analytics-context/use-analytics-context';
import usePurchaseHeaderEvent from '|/hooks/use-purchase-header-event';
import Cart from './components/cart';
import LockTimer, { LockTimerModal } from './components/lock-timer';
import styles from './purchase-header.module.css';
import { ExchangeBanner, ExchangeModal } from '../exchange-banner';
import ImpersonationBanner from '../impersonation-baner';
import SlateHeader from '../slate-header';

interface PurchaseHeaderProps {
  hideHeader?: boolean;
  orderSummary?: Summary | null;
  dashboard?: Dashboard | null;
  isOrderSummaryModalOpen?: boolean;
  setIsOrderSummaryModalOpen?: (isOpen: boolean) => void;
}

const PurchaseHeader: React.FC<PurchaseHeaderProps> = ({
  hideHeader = false,
  orderSummary = null,
  dashboard = null,
  isOrderSummaryModalOpen,
  setIsOrderSummaryModalOpen
}) => {
  const { setShowMobileNavigationRail } = useContext(MobileNavigationRailContext);
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLockTimerModalOpen, setIsLockTimerModalOpen] = useState(false);
  const { trackEvent } = useAnalyticsContext();
  const { brand } = useBrand();
  // The snapshot chrome shipped with the pp-check-it-out treatment (TXCC-9916); the live
  // control arm keeps its original support button and chip timer.
  const { isCheckItOutTreatment } = useContext(CheckItOutContext);

  usePurchaseHeaderEvent();

  if (hideHeader) return null;

  if (brand === 'slate') {
    return (
      <SlateHeader
        orderSummary={orderSummary}
        isOrderSummaryModalOpen={isOrderSummaryModalOpen}
        setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
        showMobileMenu
      />
    );
  }

  const showNavigationMenu = () => {
    trackEvent('pp_clicked_list_icon');
    setShowMobileNavigationRail(value => !value);
  };

  const onLogoClick = () => {
    trackEvent('pp_clicked_logo_icon');
    window.location.href = '/';
  };

  const onLockTimerClick = () => {
    trackEvent('pp_clicked_lock_timer');
    setIsLockTimerModalOpen(true);
  };

  const openSupportModal = () => {
    setIsSupportModalOpen(true);
  };

  return (
    <>
      <ImpersonationBanner />
      <header className={styles.header}>
        {/* Mobile Logo */}
        <button
          className={styles.mobileLogo}
          onClick={onLogoClick}
          data-analytics-id="pp_header_home"
          aria-label="Carvana Home"
        >
          <CarvanaEmblem className={styles.emblem} />
        </button>

        {/* Desktop Logo */}
        <a
          href="/"
          className={styles.desktopLogo}
          onClick={e => {
            e.preventDefault();
            onLogoClick();
          }}
          data-analytics-id="pp_header_home"
          aria-label="Carvana Home"
        >
          <CarvanaHorizontal className={styles.carvanaLogo} />
        </a>

        {/* Right Content */}
        <div
          className={`${styles.rightContent} ${isCheckItOutTreatment ? styles.rightContentCheckItOut : ''}`}
        >
          {/* Desktop Support Button */}
          <div className={styles.desktopOnly}>
            {isCheckItOutTreatment ? (
              <SupportContactLink onClick={openSupportModal} />
            ) : (
              <SupportContact onClick={openSupportModal} />
            )}
          </div>

          <LockTimer
            orderSummary={orderSummary}
            onClick={onLockTimerClick}
            plain={isCheckItOutTreatment}
            className={isCheckItOutTreatment ? styles.timerSlot : undefined}
            data-analytics-id="pp-lock-timer"
          />

          {/* Order Summary Cart */}
          <Cart
            orderSummary={orderSummary}
            dashboard={dashboard}
            isOrderSummaryModalOpen={isOrderSummaryModalOpen}
            setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
          />

          {/* Mobile Menu Icon */}
          <button
            className={styles.mobileMenuButton}
            onClick={showNavigationMenu}
            data-analytics-id="pp_header_mobile_list_icon"
            aria-label="Open navigation menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      <ExchangeBanner
        orderSummary={orderSummary}
        onSeeDetails={() => {
          setIsExchangeModalOpen(true);
        }}
      />

      {/* Exchange Modal */}
      <ExchangeModal
        isOpen={isExchangeModalOpen}
        orderSummary={orderSummary}
        onClose={() => {
          setIsExchangeModalOpen(false);
        }}
      />

      {/* Lock Timer Modal */}
      <LockTimerModal
        isOpen={isLockTimerModalOpen}
        onClose={() => {
          setIsLockTimerModalOpen(false);
        }}
      />

      {/* Support MFE Modal */}
      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
        closeMobileNavMenu={() => {
          setShowMobileNavigationRail(false);
        }}
      />
    </>
  );
};

export default PurchaseHeader;
